import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { max } from 'date-fns';
import { connect } from 'react-redux';

import { IKnmErknmFormViolation } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ViolationStatus } from 'KnmErknmPackage/ErknmViolationsModule/components/ViolationStatus';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { IconQuestion } from 'UIPackage/IconsModule/components/IconQuestion';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { booleanSelectOptions } from 'KnmPackage/MainModule/constants/booleanSelectOptions';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { AgreementStatusEnum } from 'APIPackage/KnmModule/enums/AgreementStatusEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { KnmAgreementStatusEnum } from 'KnmErknmPackage/ErknmViolationsModule/enums/KnmAgreementStatusEnum';
import { ViolationFixMethodsEnum } from 'APIPackage/KnmModule/enums/ViolationFixMethodsEnum';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { convertQuillDeltaToHtml } from '../../../functions/convertQuillDeltaToHtml';
import { IReactQuillDelta } from '../../../APIPackage/KnmModule/interfaces/IReactQuillDelta';
import { ErknmDecisionViolationFixPlanMethodsField } from '../components/ErknmDecisionViolationFixPlanMethodsField';
import { ErknmDecisionViolationFixPlanExplanationsField } from '../components/ErknmDecisionViolationFixPlanExplanationsField';
import { ErknmDecisionViolationFixItems } from '../components/ErknmDecisionViolationFixItems';
import styles from '../../../KnmErknmPackage/ErknmViolationsModule/components/ErknmViolationItem.module.scss';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IErknmAgreementsViolationItemProps {
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  violation: IKnmErknmFormViolation;
  fieldName: string;
  onRemove: () => void;
  mId: string;
}

interface IErknmAgreementsViolationItemStoreProps {
  isSupervisor: boolean;
}

const negativeSelectOption = booleanSelectOptions.find(option => !option.value);

const ErknmAgreementsViolationItemWithoutStore = ({
  violation,
  fieldName,
  onRemove,
  formRef,
  mId,
  isSupervisor,
}: IErknmAgreementsViolationItemProps & IErknmAgreementsViolationItemStoreProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const { isKnm, isPm, isDecision, agreement } = useContext(AgreementsContext);

  const isAppealed: boolean = !!violation.isAppealed?.value;
  // @ts-ignore
  const fixFactMethodInitialValue = formRef?.current.getFieldState(`${fieldName}.fixFactMethod` as keyof IKnmForm)
    ?.initial;

  const hasMobileAppInfo: boolean = !!violation.fixes?.some(
    fix => fix.method?.value === ViolationFixMethodsEnum.MOBILE_APP_INFO,
  );

  // Устранено ли в ходе другого мероприятия
  const isFixedDuringAnotherEvent = fixFactMethodInitialValue?.id === 5;

  const getFilterFixFactMethodSelectOptions = useCallback(
    (arr: ISelectOption<string | number>[] | undefined) => {
      // id = 5 (Устранено в ходе другого мероприятия)
      // отображается только при автоматически проставленном значении, ручной выбор не допускается
      return arr
        ?.map(item => ({
          ...item,
          isDisabled: item.id === 1,
        }))
        .filter(e => !isFixedDuringAnotherEvent && e.id !== 5);
    },
    [isFixedDuringAnotherEvent],
  );

  useEffect(() => {
    if (
      !!agreement?.termOfExecutionAgreement &&
      !violation.dateResolvedByAgreement &&
      agreement.status !== AgreementStatusEnum.APPROVED &&
      agreement?.status !== AgreementStatusEnum.CANCELED &&
      agreement?.status !== AgreementStatusEnum.DENIED &&
      agreement?.status !== AgreementStatusEnum.TERMINATED &&
      agreement?.status !== AgreementStatusEnum.NOT_COMPLETED
    ) {
      // @ts-ignore
      formRef?.current.change(`${fieldName}.dateResolvedByAgreement`, agreement?.termOfExecutionAgreement);
    }
  }, [agreement]);

  const agreementStatus = useMemo(() => {
    switch (agreement?.status) {
      case AgreementStatusEnum.TERMINATED:
        return KnmAgreementStatusEnum.AGREEMENT_TERMINATED;
      case AgreementStatusEnum.NOT_COMPLETED:
        return KnmAgreementStatusEnum.AGREEMENT_NOT_COMPLETED;
      default:
        return undefined;
    }
  }, [agreement]);

  const hasError = checkHasErrorInCollapseBlock(formRef, fieldName);

  const unsignedAgreementChange = useMemo(() => {
    if (!agreement) return undefined;

    const prolongateAgreementChange = agreement.changes?.filter(
      change => !!change.types?.find(type => type?.type?.value === 'EXTENSION_OF_DEADLINE'),
    );

    return prolongateAgreementChange?.find(change => !change.signed);
  }, [agreement]);

  /**
   * Возвращаем старую дату у нарушения при очистке изменения или даты в изменении
   */
  useEffect(() => {
    if (agreement?.status === AgreementStatusEnum.APPROVED) {
      if (unsignedAgreementChange) {
        if (!unsignedAgreementChange.termOfExecutionAgreement) {
          // @ts-ignore
          formRef?.current.change(`${fieldName}.dateResolvedByAgreement`, violation.previousDateResolvedByAgreement);
        }
      } else {
        // @ts-ignore
        formRef?.current.change(`${fieldName}.dateResolvedByAgreement`, violation.previousDateResolvedByAgreement);
      }
    }
  }, [unsignedAgreementChange]);

  const maxDate = useMemo(() => {
    if (!unsignedAgreementChange) return '';

    if (!unsignedAgreementChange.termOfExecutionAgreement) {
      const termOfExecutionAgreement =
        parseDate({
          date: agreement?.termOfExecutionAgreement || '',
          parseFormat: DATE_FORMAT_LONG_FNS,
        }) || new Date();

      const otherTermOfExecutionAgreements = agreement?.changes
        ?.filter(i => i.signed)
        .map(i =>
          parseDate({
            date: i.termOfExecutionAgreement || '',
            parseFormat: DATE_FORMAT_LONG_FNS,
          }),
        ) as Date[];

      return max([...otherTermOfExecutionAgreements, new Date(), termOfExecutionAgreement]);
    }

    return unsignedAgreementChange.termOfExecutionAgreement || '';
  }, [unsignedAgreementChange]);

  /**
   * Условия блокировки поля "Дата устранения по соглашению"
   */
  const isDisabledDateResolvedByAgreement = useMemo(() => {
    if (!agreement) return true;

    if (!isSupervisor) return true;

    switch (agreement.status) {
      case AgreementStatusEnum.DRAFT:
      case AgreementStatusEnum.READY_TO_APPROVE:
        return !agreement?.termOfExecutionAgreement;
      case AgreementStatusEnum.APPROVED:
        return !unsignedAgreementChange;
      default:
        return true;
    }
  }, [agreement]);

  /**
   * Условия блокировки полей, доступных к редактированию, по устранению нарушения
   */
  const isDisabledEliminationViolation = useMemo(() => {
    return (
      !isSupervisor ||
      agreement?.status !== AgreementStatusEnum.APPROVED ||
      violation.status !== ViolationStatusesEnum.SUSPENDED_BY_AGREEMENT
    );
  }, [agreement, isSupervisor]);

  /**
   * Условия блокировки поля "Нарушение устранено" с учетом изменения соглашения
   */
  const isDisabledEliminationViolationWithChanges = useMemo(() => {
    if (!agreement) return true;

    if (!isSupervisor) return true;

    if (agreement.status !== AgreementStatusEnum.APPROVED) return isDisabledEliminationViolation;

    const newChange = agreement.changes?.find(i => !i.signed);

    if (!newChange) return isDisabledEliminationViolation;

    return true;
  }, [agreement, isDisabledEliminationViolation]);

  /**
   * Условия блокировки полей "Оценка исправления нарушения"
   */
  const isDisabledRange = useMemo(() => {
    return (
      !isSupervisor ||
      agreement?.status !== AgreementStatusEnum.APPROVED ||
      (violation.status !== ViolationStatusesEnum.SUSPENDED_BY_AGREEMENT &&
        violation.status !== ViolationStatusesEnum.APPROVING)
    );
  }, [agreement, isSupervisor]);

  return (
    <KnmCollapse
      id={mId}
      title={
        <span className="shared-break-word">
          <span>Нарушение №{violation.number}:</span>{' '}
          {violation.descriptionReactQuillEditor && typeof violation.descriptionReactQuillEditor === 'object' ? (
            <div
              /* eslint-disable-next-line react/no-danger */
              dangerouslySetInnerHTML={{
                __html: convertQuillDeltaToHtml(violation.descriptionReactQuillEditor as IReactQuillDelta),
              }}
            />
          ) : (
            <>«{violation.title}»</>
          )}
        </span>
      }
      bodyClassName={styles.ItemCollapseBody}
      headerPostfix={<ViolationStatus status={violation.status} agreementStatus={agreementStatus} />}
      onClose={violation.isNew ? onRemove : undefined}
      openByDefault={violation.isNew}
      error={isKnmCollapseOpened && hasError}
      changeOpenedCallback={setIsKnmCollapseOpened}
    >
      <>
        <Row>
          <FieldBlock
            label={
              <Row align="center" margin="none" notSpace>
                <span>Нарушение устранено</span>
                <Tooltip
                  placement="right"
                  content={
                    <div className={styles.TooltipContent}>
                      Поле доступно для изменения после первого подписания предписания, а также если статус нарушения
                      имеет значение &quot;Не устранено&quot; или &quot;Ожидает подтверждения&quot;. Значение
                      &quot;Да&quot; в поле устанавливается в случае, если устранение произошло после проведения
                      мероприятия.
                    </div>
                  }
                >
                  <IconQuestion className={styles.TooltipIcon} />
                </Tooltip>
              </Row>
            }
            placementLabel="left"
            labelFontSize="small"
            color={'black'}
            className={styles.FieldBlock}
            type="not-space"
          >
            <SelectInputField
              name={`${fieldName}.isFixed`}
              options={booleanSelectOptions}
              isClearable={false}
              defaultValue={negativeSelectOption}
              disabled={isDisabledEliminationViolationWithChanges}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock
            label={
              <Row align="center" margin="none" notSpace>
                <span>Нарушение обжаловано</span>
                <Tooltip
                  placement="right"
                  content={
                    <div className={styles.TooltipContent}>
                      Поле доступно для изменения, если в представленных материалах «Оценка исправления нарушения» имеет
                      значение «Не исправлено». Значение «Да» в поле устанавливается в случае, если нарушение было
                      обжаловано в пользу заявителя.
                    </div>
                  }
                >
                  <IconQuestion className={styles.TooltipIcon} />
                </Tooltip>
              </Row>
            }
            placementLabel="left"
            labelFontSize="small"
            color={'black'}
            className={styles.FieldBlock}
            type="not-space"
          >
            <SelectInputField
              name={`${fieldName}.isAppealed`}
              options={booleanSelectOptions}
              isClearable={false}
              disabled={true}
              defaultValue={negativeSelectOption}
            />
          </FieldBlock>
        </Row>
        {isAppealed && (
          <Row>
            <FieldBlock
              label="Пояснение"
              labelFontSize="small"
              placementLabel="left"
              color={'black'}
              className={styles.FieldBlock}
              type="not-space"
            >
              <TextareaField
                name={`${fieldName}.explanation`}
                placeholder="Введите пояснение (при необходимости)"
                disabled={true}
                autoHeight
              />
            </FieldBlock>
          </Row>
        )}

        <Row margin="none">
          <FieldBlock label="Сведения о плане по устранению нарушения" />
        </Row>
        {agreement?.status !== AgreementStatusEnum.CANCELED &&
          agreement?.status !== AgreementStatusEnum.DENIED &&
          agreement?.status !== AgreementStatusEnum.TERMINATED &&
          agreement?.status !== AgreementStatusEnum.NOT_COMPLETED && (
            <Row margin="none">
              <FieldBlock
                label={'Дата устранения по соглашению'}
                placementLabel="left"
                labelFontSize="small"
                color={'black'}
                className={styles.FieldBlock}
                size={undefined}
                type="not-space"
                margin="bottom"
              >
                <DatePickerField
                  name={`${fieldName}.dateResolvedByAgreement`}
                  disabled={isDisabledDateResolvedByAgreement}
                  minDate={parseDate({
                    date: violation.fixPlanDates?.[0].value || '',
                    parseFormat: DATE_FORMAT_LONG_FNS,
                  })}
                  maxDate={parseDate({
                    date: maxDate,
                    parseFormat: DATE_FORMAT_LONG_FNS,
                  })}
                />
              </FieldBlock>
            </Row>
          )}
        <Row margin="none">
          <FieldBlock
            label={'Плановая дата устранения (по предписанию)'}
            placementLabel="left"
            labelFontSize="small"
            color={'black'}
            className={cn(styles.FieldBlock, styles.PositionRelativeTop)}
            size={undefined}
            type="not-space"
          >
            {agreement?.status === AgreementStatusEnum.APPROVED && (
              <div className={styles.FixPlanDatesHint}>
                <Hint text="Дата приостановлена" />
              </div>
            )}
            <DatePickerField name={`${fieldName}.fixPlanDates[0].value`} disabled={true} />
          </FieldBlock>
        </Row>
        <Row margin="none">
          <ErknmDecisionViolationFixPlanMethodsField
            fieldName={fieldName}
            formRef={formRef}
            violation={violation}
            watchOnly={true}
            isPaperless={true}
            mId={mId}
            isKnm={isKnm}
            isPm={isPm}
            isDecision={isDecision}
          />
        </Row>
        <Row margin="none">
          <ErknmDecisionViolationFixPlanExplanationsField
            fieldName={fieldName}
            formRef={formRef}
            violation={violation}
            watchOnly={true}
            isPaperless={true}
            mId={mId}
            isKnm={isKnm}
            isPm={isPm}
            isDecision={isDecision}
          />
        </Row>
        {violation.isFixed?.value && (
          <>
            <Row>
              <FieldBlock
                label="Фактическая дата устранения"
                placementLabel="left"
                labelFontSize="small"
                type="not-space"
                color={'black'}
                className={styles.FieldBlock}
              >
                <DatePickerField name={`${fieldName}.fixFactDate`} disabled />
              </FieldBlock>
            </Row>
            {!hasMobileAppInfo && (
              <Row>
                <FieldBlock
                  label={getTitleFieldWithRedStar({
                    label: `Фактический способ устранения нарушения`,
                    field: `${fieldName}.fixFactMethod`,
                    isPm,
                    isDecision,
                    isKnm,
                  })}
                  placementLabel="left"
                  labelFontSize="small"
                  type="not-space"
                  color={'black'}
                  className={styles.FieldBlock}
                  id={`${mId}.fixFactMethod`}
                >
                  <SelectInputFieldAutoOptions
                    id={`${fieldName}.fixFactMethod`}
                    name={`${fieldName}.fixFactMethod`}
                    optionName={CommonCatalogsNamesEnum.VIOLATION_FIX_METHOD}
                    transformFunc={getFilterFixFactMethodSelectOptions}
                    isClearable={false}
                    disabled={
                      isDisabledEliminationViolation && violation.isFixed?.value ? fixFactMethodInitialValue : undefined
                    }
                  />
                </FieldBlock>
              </Row>
            )}
          </>
        )}
        {(violation.isFixed?.value || !!violation.fixes?.length) && (
          <ErknmDecisionViolationFixItems
            violation={violation}
            formRef={formRef}
            name={fieldName}
            isFixedFieldName="isFixed"
            watchOnly={isDisabledEliminationViolation}
            watchOnlyRange={isDisabledRange}
            isPaperless={true}
            id={mId}
            inAgreement
            isSupervisor={isSupervisor}
            isFixed={violation.isFixed?.value}
            isKnm={isKnm}
            isPm={isPm}
            isDecision={isDecision}
          />
        )}
      </>
    </KnmCollapse>
  );
};

const mapStateToProps = (state: IRootState): IErknmAgreementsViolationItemStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
});

export const ErknmAgreementsViolationItem = connect(mapStateToProps)(ErknmAgreementsViolationItemWithoutStore);
